import { CheckboxSelect, CheckboxSelectOption } from '@widgets/index';
import { FC, useEffect, useMemo, useState } from 'react';
import { ISort } from '@entities/util/ISort';
import CustomRangeDatePicker from '@widgets/range-date-picker-with-modes';
import { useManagerOptions } from '@app/queries/options/useManagerOptions';
import { useRegionOptions } from '@app/queries/options/useRegionOptions';
import { useSourceOptions } from '@app/queries/options/useSourceOptions';
import { useTagOptions } from '@app/queries/options/useTagOptions';
import { useSelector } from 'react-redux';
import { selectLastUploadedReportAt } from '@app/redux/features/userSlice';
import { getDateShortcuts, parseDDMMYYYY } from '@app/utils/date';
import IconResetFilters from '@shared/icons/IconResetFilters';
import { DEFAULT_PER_PAGE } from '@widgets/paginator';
import { useCoinOptions } from '@app/queries/options/useCoinOptions';

export interface IIncomeHashRateFilter {
  dateFrom: string;
  dateTo: string;
  managers: string[];
  regions: string[];
  sources: string[];
  tags: string[];
  coins: string[];

  page: number;
  sort: ISort | null;
  perPage: number;
}

export const IncomeHashRateDefaultFilter: IIncomeHashRateFilter = {
  dateFrom: '',
  dateTo: '',
  managers: [],
  regions: [],
  sources: [],
  tags: [],
  coins: [],

  page: 1,
  sort: null,
  perPage: DEFAULT_PER_PAGE,
};

interface IProps {
  onChangeFilters: (values: IIncomeHashRateFilter) => void;
  filters: IIncomeHashRateFilter;
  onReset: () => void;
}

const IncomeHashRateFilter: FC<IProps> = ({ onChangeFilters, filters, onReset }) => {
  const managerOptionsFetcher = useManagerOptions();
  const regionOptionsFetcher = useRegionOptions();
  const sourceOptionsFetcher = useSourceOptions({ includeHidden: true });
  const tagOptionsFetcher = useTagOptions({ includeHidden: true });
  const coinOptionsFetcher = useCoinOptions();

  const [managerOptions, setManagerOptions] = useState<CheckboxSelectOption[]>();
  const [regionOptions, setRegionOptions] = useState<CheckboxSelectOption[]>();
  const [sourceOptions, setSourceOptions] = useState<CheckboxSelectOption[]>();
  const [tagOptions, setTagOptions] = useState<CheckboxSelectOption[]>();
  const [coinOptions, setCoinOptions] = useState<CheckboxSelectOption[]>();

  const reportLastSyncDate = useSelector(selectLastUploadedReportAt);

  useEffect(() => {
    (async () => {
      const data = await managerOptionsFetcher.fetchData();
      setManagerOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await regionOptionsFetcher.fetchData();
      setRegionOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await sourceOptionsFetcher.fetchData();
      setSourceOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await tagOptionsFetcher.fetchData();
      setTagOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await coinOptionsFetcher.fetchData();
      setCoinOptions(data);
    })();
  }, []);

  const customDateShortcuts = useMemo(() => {
    if (!reportLastSyncDate) return;

    const parsedReportLastSyncDate = parseDDMMYYYY(reportLastSyncDate);
    if (Number.isNaN(parsedReportLastSyncDate.getTime())) return undefined;

    return {
      ...getDateShortcuts(parsedReportLastSyncDate),
      all: {
        text: 'Все',
        period: {
          start: new Date(new Date().setTime(1639699202000)),
          end: new Date(),
        },
      },
    };
  }, [reportLastSyncDate]);

  return (
    <div className="flex flex-wrap gap-[10px] items-center mb-[20px] mt-[5px]">
      <div className="cursor-pointer" onClick={() => onReset()}>
        <IconResetFilters />
      </div>
      <CustomRangeDatePicker
        onChange={values =>
          onChangeFilters({
            ...filters,
            dateFrom: values.startDate,
            dateTo: values.endDate,
          })
        }
        /** @ts-ignore */
        maxDate={
          reportLastSyncDate ? new Date(reportLastSyncDate.split('.').reverse().join('-')) : ''
        }
        width={180}
        shortcuts={customDateShortcuts}
        value={{ startDate: filters?.dateFrom, endDate: filters?.dateTo }}
      />

      <CheckboxSelect
        title="Менеджер"
        value={
          managerOptions?.length
            ? managerOptions?.filter(item => filters.managers?.includes(item.value))
            : []
        }
        isLoading={!managerOptionsFetcher.isSuccess}
        options={managerOptions}
        onChange={values =>
          onChangeFilters({ ...filters, managers: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        title="Регион"
        value={
          regionOptions?.length
            ? regionOptions?.filter(item => filters.regions?.includes(item.value))
            : []
        }
        isLoading={!regionOptionsFetcher.isSuccess}
        options={regionOptions}
        onChange={values =>
          onChangeFilters({ ...filters, regions: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        value={
          sourceOptions?.length
            ? sourceOptions?.filter(item => filters.sources?.includes(item.value))
            : []
        }
        isLoading={!sourceOptionsFetcher.isSuccess}
        title="Источник"
        options={sourceOptions}
        onChange={values =>
          onChangeFilters({ ...filters, sources: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        isLoading={!tagOptionsFetcher.isSuccess}
        value={
          tagOptions?.length ? tagOptions?.filter(item => filters.tags?.includes(item.value)) : []
        }
        title="Тег"
        options={tagOptions}
        onChange={values => onChangeFilters({ ...filters, tags: values.map(item => item.value) })}
      />
      <CheckboxSelect
        isLoading={!coinOptionsFetcher.isSuccess}
        value={
          coinOptions?.length
            ? coinOptions?.filter(item => filters?.coins?.includes(item.value))
            : []
        }
        title="Монета"
        options={coinOptions}
        onChange={values => onChangeFilters({ ...filters, coins: values.map(item => item.value) })}
      />
    </div>
  );
};

export default IncomeHashRateFilter;
